import React from 'react';
import { useSelector } from 'react-redux';
import { getBootstrapHotelsSelector } from 'store/modules/bootstrap/selectors';
import { TextInput } from 'ui/TextInput';
import { Multiselect } from 'ui/Multiselect';
import { EditorQuill } from 'ui/EditorQuill/EditorQuill';
import styled from 'styled-components';

const QuillErrorWrapper = styled.div`
  &.quill-error-state {
    border: 1px solid #d4244e !important;
    background-color: #FFF0F3 !important;
    // .ql-container {
    //   border-color: #d4244e !important;
    // }
  }
`;

interface IMainInfoSectionProps {
  hotelUuid: string;
  title: string;
  description: string;
  validationErrors: {
    hotelUuid?: boolean;
    title?: boolean;
    description?: boolean;
  };
  updateHotelUuid: (hotelUuid: string) => void;
  updateTitle: (title: string) => void;
  updateDescription: (description: string) => void;
}

export const MainInfoSection = ({
  hotelUuid,
  title,
  description,
  validationErrors,
  updateHotelUuid,
  updateTitle,
  updateDescription,
}: IMainInfoSectionProps) => {
  // Get hotels from Redux bootstrap
  const allHotels = useSelector(getBootstrapHotelsSelector);

  // Transform hotels for Multiselect options
  const hotelOptions = allHotels.map(hotel => ({
    value: hotel.uuid,
    label: hotel.name,
  }));

  return (
    <div className="flex flex-col gap-20px">
      {/* Section title */}
      <div className="flex flex-col gap-10px">
        <span className="block font-pt-sans font-bold text-17px leading-22px text-black">Main Info</span>

        {/* Hotel and Title row */}
        <div className="flex flex-row gap-20px">
          {/* Hotel field */}
          <div className="flex flex-col gap-5px flex-1" data-validation-id="hotelUuid">
            <label className="font-pt-sans text-13px leading-17px tracking-2xs text-black">Hotel *</label>
            <Multiselect
              options={hotelOptions}
              selectedValues={hotelUuid ? [hotelUuid] : []}
              onUpdate={values => updateHotelUuid(values[0] || '')}
              isSingleSelectMode={true}
              isCloseOnSelect={true}
              isEnableFuzzySearch={true}
              hideCheckboxes={true}
              placeholderText="Select a hotel"
              itemsClassname="bg-white-hard"
              itemCtaClassName="hover:bg-gray-10"
              height={35}
              borderColourClass={validationErrors.hotelUuid ? 'border-red-95' : 'border-gray-40'}
              primaryCtaClassName={validationErrors.hotelUuid ? 'bg-red-25' : 'bg-ivory'}
              hideNonMatchingOptions={true}
            />
            {validationErrors.hotelUuid && (
              <p className="text-red-95 text-13px leading-2xs font-pt-sans mt-0 mb-0">Field is required</p>
            )}
          </div>

          {/* Title field */}
          <div className="flex-1" data-validation-id="title">
            <TextInput
              id="announcement-title"
              label="Title *"
              value={title}
              onChange={e => updateTitle(e.currentTarget.value)}
              placeholder="Enter announcement title"
              errorMessage={validationErrors.title ? 'Field is required' : undefined}
              inputClassName="h-35px"
            />
          </div>
        </div>
      </div>

      {/* Description field */}
      <div className="flex flex-col gap-5px" data-validation-id="description">
        <label className="font-pt-sans text-13px leading-17px tracking-2xs text-black">Description *</label>
        <QuillErrorWrapper className={validationErrors.description ? 'quill-error-state' : ''}>
          <EditorQuill
            text={description}
            onChange={updateDescription}
            placeholder="Enter announcement description..."
            minHeight={120}
            enableLinks={true}
          />
        </QuillErrorWrapper>
        {validationErrors.description && (
          <p className="text-red-95 text-13px leading-2xs font-pt-sans mt-0 mb-0">Field is required</p>
        )}
      </div>
    </div>
  );
};
